import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL, CURRENCIES, CURRENCY_LABELS, COUNTRY_CONFIGS } from './AdminShared';
import { InvitationMessages } from './AdminDataTabs';
import { Gift, CheckCircle } from 'lucide-react';

export const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    default_currency: 'GBP', seller_match_fee: 45, buyer_verification_fee: 20,
    professional_access_fee: 25, tenant_fee: 0, exchange_rates: {},
    mock_payments: false, disable_stripe_link: false, free_trial_enabled: false, country: 'UK'
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/settings`);
      setSettings(res.data);
      setFormData({
        default_currency: res.data.default_currency || 'GBP',
        seller_match_fee: res.data.seller_match_fee ?? 45,
        buyer_verification_fee: res.data.buyer_verification_fee ?? 20,
        professional_access_fee: res.data.professional_access_fee ?? 25,
        tenant_fee: res.data.tenant_fee ?? 0,
        exchange_rates: res.data.exchange_rates || {},
        mock_payments: res.data.mock_payments || false,
        disable_stripe_link: res.data.disable_stripe_link || false,
        free_trial_enabled: res.data.free_trial_enabled || false,
        country: res.data.country || 'UK'
      });
    } catch (error) { toast.error('Failed to load settings'); }
  };

  const handleCountryChange = (country) => {
    const config = COUNTRY_CONFIGS[country];
    if (config) {
      setFormData(prev => ({
        ...prev, country, default_currency: config.currency,
        seller_match_fee: config.fees.seller, buyer_verification_fee: config.fees.buyer,
        professional_access_fee: config.fees.pro, tenant_fee: config.fees.tenant
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings`, formData);
      toast.success('Settings saved successfully!');
      fetchSettings();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save settings'); }
    finally { setSaving(false); }
  };

  const updateRate = (currency, value) => {
    setFormData(prev => ({ ...prev, exchange_rates: { ...prev.exchange_rates, [currency]: parseFloat(value) || 0 } }));
  };

  if (!settings) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-8">
      {/* Country Selector */}
      <div className="luxury-card p-8">
        <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Market Region</h3>
        <p className="text-sm text-slate-500 mb-6">Switch between country-specific fee structures, currencies, and compliance rules</p>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(COUNTRY_CONFIGS).map(([code, config]) => (
            <button key={code} onClick={() => handleCountryChange(code)}
              className={`p-5 border-2 transition-all text-left ${formData.country === code ? 'border-[#E07A3A] bg-[#FEF3EC]' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
              data-testid={`country-select-${code.toLowerCase()}`}>
              <span className="text-xl font-bold">{COUNTRY_CONFIGS[code]?.flag || code}</span>
              <p className="text-[#1A1A1A] font-semibold mt-2">{config.label}</p>
              <p className="text-xs text-slate-500 mt-1">{CURRENCY_LABELS[config.currency]} &middot; {config.tenantLaw}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Currency & Fees */}
      <div className="luxury-card p-8">
        <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Currency & {COUNTRY_CONFIGS[formData.country]?.label || formData.country} Fee Structure</h3>
        <p className="text-sm text-slate-500 mb-6">Platform access fees — all editable. Tenant fees must remain at 0 ({COUNTRY_CONFIGS[formData.country]?.tenantLaw || 'Platform Policy'}).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm tracking-wider uppercase text-slate-500 mb-2 font-medium">Default Currency</label>
            <select value={formData.default_currency} onChange={(e) => setFormData(prev => ({ ...prev, default_currency: e.target.value }))}
              className="w-full p-3 border border-slate-200 text-[#1A1A1A] bg-white focus:border-[#E07A3A] focus:outline-none" data-testid="settings-currency-select">
              {CURRENCIES.map(c => <option key={c} value={c}>{CURRENCY_LABELS[c]}</option>)}
            </select>
          </div>
          {[
            { key: 'seller_match_fee', label: 'Seller Match Fee', desc: 'Seller pays to unlock pre-verified buyer/tenant profile' },
            { key: 'buyer_verification_fee', label: 'Buyer Verification Fee', desc: 'Buyer pays for identity verification' },
            { key: 'professional_access_fee', label: 'Professional Access Fee', desc: 'Solicitors/agents pay for secure chat & data access' },
          ].map(({ key, label, desc }) => (
            <div key={key}>
              <label className="block text-sm tracking-wider uppercase text-slate-500 mb-2 font-medium">{label}</label>
              <input type="number" min="0" max="500" value={formData[key]}
                onChange={(e) => setFormData(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                className="w-full p-3 border border-slate-200 text-[#1A1A1A] bg-white focus:border-[#E07A3A] focus:outline-none"
                data-testid={`settings-${key.replace(/_/g, '-')}`} />
              <p className="text-xs text-slate-400 mt-1">{desc}</p>
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="block text-sm tracking-wider uppercase text-slate-500 mb-2 font-medium">Tenant Fee</label>
            <input type="number" min="0" max="500" value={formData.tenant_fee}
              onChange={(e) => setFormData(prev => ({ ...prev, tenant_fee: parseInt(e.target.value) || 0 }))}
              className="w-full p-3 border border-slate-200 text-[#1A1A1A] bg-white focus:border-[#E07A3A] focus:outline-none" data-testid="settings-tenant-fee" />
            <p className="text-xs text-amber-600 mt-1 font-medium">{COUNTRY_CONFIGS[formData.country]?.tenantLaw || 'Platform Policy'}: Must be 0 for tenants</p>
          </div>
        </div>
      </div>

      {/* Payment Mode */}
      <div className="luxury-card p-8">
        <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Payment Mode</h3>
        <p className="text-sm text-slate-500 mb-6">Switch between live Stripe payments and simulated demo payments</p>
        <div className="flex items-center gap-4">
          <button onClick={() => setFormData(prev => ({ ...prev, mock_payments: !prev.mock_payments }))}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${formData.mock_payments ? 'bg-[#E07A3A]' : 'bg-slate-300'}`} data-testid="mock-payments-toggle">
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${formData.mock_payments ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
          <div>
            <span className={`text-sm font-semibold ${formData.mock_payments ? 'text-[#E07A3A]' : 'text-slate-600'}`}>
              {formData.mock_payments ? 'MOCK MODE — No real charges' : 'LIVE MODE — Real Stripe payments'}
            </span>
            <p className="text-xs text-slate-400 mt-0.5">{formData.mock_payments ? 'Payments are simulated instantly. No Stripe redirect. Good for testing.' : 'Payments go through Stripe checkout with real card charges.'}</p>
          </div>
        </div>
        {formData.mock_payments && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700 font-medium">Mock mode is ON. All payments will be simulated — no real money will be charged. Remember to switch back to LIVE before going to production.</p>
          </div>
        )}
        {/* Stripe Link Toggle */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <h4 className="text-sm font-semibold text-[#1A1A1A] mb-1">Stripe Link (Auto-Save Cards)</h4>
          <p className="text-xs text-slate-400 mb-4">Prevent Stripe from prompting customers to save their card details via Link</p>
          <div className="flex items-center gap-4">
            <button onClick={() => setFormData(prev => ({ ...prev, disable_stripe_link: !prev.disable_stripe_link }))}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${formData.disable_stripe_link ? 'bg-red-500' : 'bg-green-500'}`} data-testid="disable-stripe-link-toggle">
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${formData.disable_stripe_link ? 'translate-x-8' : 'translate-x-1'}`} />
            </button>
            <div>
              <span className={`text-sm font-semibold ${formData.disable_stripe_link ? 'text-red-600' : 'text-green-600'}`}>
                {formData.disable_stripe_link ? 'Link DISABLED — Cards will not be auto-saved' : 'Link ENABLED — Stripe may offer to save cards'}
              </span>
              <p className="text-xs text-slate-400 mt-0.5">{formData.disable_stripe_link ? 'Checkout will only show card payment without Link auto-save prompts.' : 'Stripe may show a "Save your info" prompt during checkout.'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Free Trial Mode */}
      <div className="luxury-card p-8" data-testid="free-trial-section">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="h-6 w-6 text-green-500" />
          <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Free Trial Mode</h3>
        </div>
        <p className="text-sm text-slate-500 mb-6">Enable free trial to waive all platform fees. Revenue sections stay visible but blurred. Payments are auto-approved for free.</p>
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setFormData(prev => ({ ...prev, free_trial_enabled: !prev.free_trial_enabled }))}
            className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${formData.free_trial_enabled ? 'bg-green-500' : 'bg-slate-300'}`} data-testid="free-trial-toggle">
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${formData.free_trial_enabled ? 'translate-x-9' : 'translate-x-1'}`} />
          </button>
          <div>
            <span className={`text-base font-bold ${formData.free_trial_enabled ? 'text-green-600' : 'text-slate-400'}`}>
              {formData.free_trial_enabled ? 'FREE TRIAL IS ON' : 'Free Trial is OFF'}
            </span>
            <p className="text-xs text-slate-400 mt-0.5">{formData.free_trial_enabled ? 'All users can register, browse, and transact without paying any fees.' : 'Normal fee structure applies. Users pay to verify and connect.'}</p>
          </div>
        </div>
        {formData.free_trial_enabled && (
          <div className="p-4 bg-green-50 border border-green-200 mb-6">
            <p className="text-sm text-green-700 font-medium"><CheckCircle className="h-4 w-4 inline mr-2" />Trial is active. All platform fees are waived. The landing page shows a green "FREE TRIAL" banner and fee sections are blurred. Remember to turn this OFF before going live with paid operations.</p>
          </div>
        )}
      </div>

      {/* Invitation Links */}
      <InvitationMessages siteUrl={typeof window !== 'undefined' ? window.location.origin : ''} freeTrial={formData.free_trial_enabled} />

      {/* Exchange Rates */}
      <div className="luxury-card p-8">
        <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Exchange Rates</h3>
        <p className="text-sm text-slate-500 mb-6">Rates relative to base currency</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CURRENCIES.map(c => (
            <div key={c}>
              <label className="block text-sm tracking-wider uppercase text-slate-500 mb-1 font-medium">{CURRENCY_LABELS[c]}</label>
              <input type="number" step="0.0001" min="0" value={formData.exchange_rates[c] || ''}
                onChange={(e) => updateRate(c, e.target.value)}
                className="w-full p-3 border border-slate-200 text-[#1A1A1A] bg-white focus:border-[#E07A3A] focus:outline-none"
                data-testid={`settings-rate-${c.toLowerCase()}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-10 py-6 text-sm tracking-widest uppercase font-semibold" data-testid="settings-save-btn">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};
